/**
 * SavedViewStrip — the desktop (≥ md) presentation of the saved-view
 * selector: a single-line strip of pills beside the page title. The "All"
 * pill (or built-in view pills), the saved views that fit, a "More" pill for
 * the ones that don't, and — behind a divider — a trailing "New view" text
 * action to save the current view.
 *
 * The strip never wraps. It takes the width left beside the title, measures
 * its pills (`useOverflow`, the same primitive as the record page's tab
 * bar), and collapses trailing views into "More" when they don't fit. The
 * active view is pinned: if it would land in "More" it takes the last
 * visible slot instead, so the header always names the view you're on.
 *
 * Owned views expose rename / share / delete via a caret menu revealed on
 * hover; the active view's menu also carries Save / Discard while it has
 * unsaved changes. Shared-by-others views are apply-only.
 */

import { Plus } from '@untitledui/icons';
import { type ReactNode, useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { useOverflow } from '@/lib/base/tabs';

import { stripItemToActiveView } from './buildViewHref';
import {
  AllViewPill,
  BuiltInViewPill,
  MoreViewsPill,
  SavedViewPill,
} from './SavedViewPills';
import {
  isDefaultRef,
  type SavedViewPresentationProps,
  type StripItem,
} from './savedViewTypes';

// ---------------------------------------------------------------------------
// Hoisted constants
// ---------------------------------------------------------------------------

/** Flex gap between pills (`gap-0.5`), in px. */
const PILL_GAP = 2;
/** The "More" pill's footprint including the gap before it, in px. */
const MORE_TRIGGER_WIDTH = 72;
/**
 * Room for ONE pill to grow its hover caret: gap-1.5 + the caret box (p-0.5
 * + 0.7rem icon, -mr-0.5) = 19.2px at 16px root. Reserved out of the
 * MEASUREMENT budget so revealing a caret can never push content into the
 * clipped edge — the reason pills grow on hover instead of reserving width
 * each (#12538). Layout-wise the growth shifts whatever sits right of the
 * hovered pill (including "New view") while the caret shows, exactly as
 * pills right of a hovered pill always have; the former trailing-padding
 * scheme that pinned "New view" left a permanent ~21px hole after the last
 * pill, which read as broken spacing.
 */
const CARET_RESERVE = 20;
/** The divider between the All / built-in pills and the saved views (`mx-1` + 1px). */
const DIVIDER_WIDTH = 9;
/** The "New view" action's footprint including its divider + the gaps, in px. */
const NEW_VIEW_WIDTH = 92;

// The measured container. `flex-1` gives it a width that does not depend on
// its content — measuring a content-sized box shrinks it with every pill it
// collapses and cascades to nothing.
const STRIP_CLASS = 'flex min-w-0 flex-1 items-center gap-0.5';

// The pills area is content-sized so "New view" sits snug after the last
// pill instead of at the far edge of the row. Caret growth on hover shifts
// "New view" along with everything else right of the hovered pill (see
// CARET_RESERVE above).
// `overflow-x-clip` (not `hidden`) so the active pill's shadow can still
// paint below the strip's box; only the inline axis is clipped.
const PILLS_CLASS =
  'flex min-w-0 items-center gap-0.5 overflow-x-clip overflow-y-visible';

// "New view" is the library's quiet text action (Button `link-gray`), NOT a
// pill — it creates, the pills select, and styling it as just another
// inactive pill made it read as a view. Two things tell it apart: the plain
// text-link styling, and the strip's existing zoning trait — the same
// hairline divider that separates the All / built-in pills from the saved
// views also separates the views from this action. (The bare "+" glyph it
// replaced long ago was too faint and unnamed to be discoverable; "Save
// view" read as "save THIS view".) It opens the "Save current view" modal.
// Its width (and its divider's) is part of the measurement reserve so it
// never collapses into "More" — the strip gives up a view instead.
const NEW_VIEW_ICON_CLASS = '!size-[0.8rem]';

const DIVIDER_CLASS =
  'mx-1 h-4 w-px shrink-0 bg-[var(--color-border-secondary)]';

// ---------------------------------------------------------------------------
// Partition: which items render inline, which collapse into "More"
// ---------------------------------------------------------------------------

interface Partition {
  /** Inline items with their ORIGINAL index (the measurement cache key). */
  visible: Array<{ item: StripItem; index: number }>;
  overflow: StripItem[];
}

function partitionItems(
  items: StripItem[],
  visibleCount: number,
  pinnedIndex: number
): Partition {
  const count = Math.min(visibleCount, items.length);
  const visibleIndexes: number[] = [];
  if (pinnedIndex < 0 || pinnedIndex < count) {
    for (let i = 0; i < count; i++) {
      visibleIndexes.push(i);
    }
  } else {
    for (let i = 0; i < count - 1; i++) {
      visibleIndexes.push(i);
    }
    visibleIndexes.push(pinnedIndex);
  }
  const visibleSet = new Set(visibleIndexes);
  return {
    visible: visibleIndexes.map(index => ({ item: items[index], index })),
    overflow: items.filter((_, i) => !visibleSet.has(i)),
  };
}

// ---------------------------------------------------------------------------
// Strip
// ---------------------------------------------------------------------------

export function SavedViewStrip({
  activeViewId,
  orderedViews,
  builtInViews,
  activeBuiltInViewId,
  onSelectBuiltInView,
  onSelectView,
  onSelectAll,
  onSaveCurrentView,
  defaultViewRef,
  hasDefaultViewFeature,
  onToggleDefaultAll,
  onToggleDefaultBuiltIn,
  onToggleDefaultSaved,
  onRename,
  onToggleShare,
  onDelete,
  isMutationPending,
  viewDirty,
  dirtyActions,
  canCreateView,
  hrefForView,
}: SavedViewPresentationProps) {
  const hrefForItem = useCallback(
    (item: StripItem) => hrefForView(stripItemToActiveView(item)),
    [hrefForView]
  );

  const items = useMemo<StripItem[]>(() => {
    const head: StripItem[] = builtInViews
      ? builtInViews.map(view => ({
          key: `builtin:${view.id}`,
          kind: 'builtin',
          view,
        }))
      : [{ key: 'all', kind: 'all' }];
    const saved: StripItem[] = orderedViews.map(view => ({
      key: `saved:${view.entity_id}`,
      kind: 'saved',
      view,
    }));
    return [...head, ...saved];
  }, [builtInViews, orderedViews]);

  const activeIndex = items.findIndex(item => {
    switch (item.kind) {
      case 'all':
        return activeViewId === null;
      case 'builtin':
        return activeViewId === null && item.view.id === activeBuiltInViewId;
      case 'saved':
        return item.view.entity_id === activeViewId;
    }
  });

  const { containerRef, setItemRef, visibleCount } = useOverflow(items.length, {
    gap: PILL_GAP,
    minVisible: 1,
    triggerWidth: MORE_TRIGGER_WIDTH,
    reserve:
      (canCreateView ? NEW_VIEW_WIDTH : 0) +
      CARET_RESERVE +
      (orderedViews.length > 0 ? DIVIDER_WIDTH : 0),
    pinnedIndex: activeIndex >= 0 ? activeIndex : undefined,
    // A dirty view's caret stays showing and widens its pill; re-measure
    // when that toggles.
    contentKey: viewDirty,
  });

  const { visible, overflow } = partitionItems(
    items,
    visibleCount,
    activeIndex
  );

  const hasSavedViews = orderedViews.length > 0;
  const isAllDefault = isDefaultRef(defaultViewRef, { kind: 'all' });

  // The divider sits after the last inline All / built-in pill whenever there
  // are saved views (inline or in "More") to separate them from.
  const lastHeadVisibleIndex = visible.reduce(
    (acc, entry, i) => (entry.item.kind === 'saved' ? acc : i),
    -1
  );

  const inlinePills: ReactNode[] = [];
  visible.forEach((entry, position) => {
    const { item, index } = entry;
    let pill: ReactNode;
    switch (item.kind) {
      case 'all':
        pill = (
          <AllViewPill
            href={hrefForItem(item)}
            isActive={activeViewId === null}
            isDefault={isAllDefault}
            onToggleDefault={
              hasDefaultViewFeature ? onToggleDefaultAll : undefined
            }
            onSelect={onSelectAll}
          />
        );
        break;
      case 'builtin':
        pill = (
          <BuiltInViewPill
            view={item.view}
            href={hrefForItem(item)}
            isActive={
              activeViewId === null && activeBuiltInViewId === item.view.id
            }
            isDefault={isDefaultRef(defaultViewRef, {
              kind: 'builtin',
              id: item.view.id,
            })}
            onSelect={onSelectBuiltInView ?? onSelectAll}
            onToggleDefault={
              hasDefaultViewFeature ? onToggleDefaultBuiltIn : undefined
            }
          />
        );
        break;
      case 'saved':
        pill = (
          <SavedViewPill
            view={item.view}
            href={hrefForItem(item)}
            isActive={activeViewId === item.view.entity_id}
            isDefault={isDefaultRef(defaultViewRef, {
              kind: 'saved',
              id: item.view.entity_id,
            })}
            isDirty={viewDirty && activeViewId === item.view.entity_id}
            dirtyActions={dirtyActions}
            onApply={onSelectView}
            onRename={onRename}
            onToggleShare={onToggleShare}
            onDelete={onDelete}
            onToggleDefault={
              hasDefaultViewFeature ? onToggleDefaultSaved : undefined
            }
            isMutationPending={isMutationPending}
          />
        );
        break;
    }
    inlinePills.push(
      <div key={item.key} ref={setItemRef(index)} className='flex shrink-0'>
        {pill}
      </div>
    );
    if (hasSavedViews && position === lastHeadVisibleIndex) {
      inlinePills.push(
        <span key='divider' className={DIVIDER_CLASS} aria-hidden='true' />
      );
    }
  });

  return (
    <div
      ref={containerRef}
      role='group'
      aria-label='Views'
      className={STRIP_CLASS}
    >
      <div className={PILLS_CLASS}>
        {inlinePills}
        {overflow.length > 0 && (
          <MoreViewsPill
            items={overflow}
            defaultViewRef={defaultViewRef}
            hrefForItem={hrefForItem}
            onSelectAll={onSelectAll}
            onSelectBuiltInView={onSelectBuiltInView}
            onSelectView={onSelectView}
          />
        )}
      </div>
      {canCreateView && (
        <>
          <span className={DIVIDER_CLASS} aria-hidden='true' />
          <Button
            color='link-gray'
            size='xs'
            iconLeading={Plus}
            iconLeadingClassName={NEW_VIEW_ICON_CLASS}
            className='shrink-0'
            onClick={onSaveCurrentView}
          >
            New view
          </Button>
        </>
      )}
    </div>
  );
}
